import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { 
  Zap, 
  Sun, 
  Cloud,
  CloudRain,
  CloudSun,
  LogOut,
  Calendar,
  TrendingUp,
  Clock,
  RefreshCw,
  ChevronRight,
  Leaf,
  TreeDeciduous,
  Car,
  Plane
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Forecasts = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [solarForecast, setSolarForecast] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [carbonFootprint, setCarbonFootprint] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [installationKwc, setInstallationKwc] = useState(6);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [forecastRes, scheduleRes, carbonRes, comparisonRes] = await Promise.all([
        axios.get(`${API_URL}/api/forecast/solar?installation_kwc=${installationKwc}&days=7`),
        axios.get(`${API_URL}/api/forecast/schedule?installation_kwc=${installationKwc}`),
        axios.get(`${API_URL}/api/carbon/footprint`),
        axios.get(`${API_URL}/api/comparison/grid?days=30`)
      ]);
      
      setSolarForecast(forecastRes.data);
      setSchedule(scheduleRes.data);
      setCarbonFootprint(carbonRes.data);
      setComparison(comparisonRes.data.comparison || comparisonRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  const getWeatherIcon = (code) => {
    switch (code) {
      case 'sunny': return <Sun className="w-6 h-6 text-yellow-500" />;
      case 'partly_cloudy': return <CloudSun className="w-6 h-6 text-blue-400" />;
      case 'cloudy': return <Cloud className="w-6 h-6 text-gray-400" />;
      case 'rainy': return <CloudRain className="w-6 h-6 text-blue-600" />;
      default: return <Sun className="w-6 h-6 text-yellow-500" />;
    }
  };

  const getWeatherBg = (code) => {
    switch (code) {
      case 'sunny': return 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200';
      case 'partly_cloudy': return 'bg-gradient-to-br from-blue-50 to-gray-50 border-blue-200';
      case 'cloudy': return 'bg-gradient-to-br from-gray-100 to-gray-50 border-gray-300';
      case 'rainy': return 'bg-gradient-to-br from-blue-100 to-gray-100 border-blue-300';
      default: return 'bg-white';
    }
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  // Prepare chart data
  const chartData = solarForecast.map(f => ({
    date: new Date(f.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
    production: f.estimated_production_kwh,
    confidence: f.confidence_percent
  }));

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/forecasts" className="nav-link active">Prévisions</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="page-title">
                Prévisions & Optimisation
              </h1>
              <p className="text-muted-foreground">
                Anticipez votre production et optimisez vos échanges
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Installation:</span>
                <Badge variant="outline" className="font-mono">{installationKwc} kWc</Badge>
              </div>
              <Button 
                onClick={fetchData}
                variant="outline"
                className="gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Actualiser
              </Button>
            </div>
          </div>

          {/* Installation Size Slider */}
          <Card className="stats-card mb-8" data-testid="installation-config">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground">Taille de l'installation</h3>
                  <p className="text-sm text-muted-foreground">Ajustez pour personnaliser les prévisions</p>
                </div>
                <span className="text-2xl font-bold text-primary">{installationKwc} kWc</span>
              </div>
              <Slider
                value={[installationKwc]}
                onValueChange={(v) => setInstallationKwc(v[0])}
                min={1}
                max={20}
                step={0.5}
                className="py-4"
                data-testid="installation-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1 kWc</span>
                <span>20 kWc</span>
              </div>
            </CardContent>
          </Card>

          {/* Solar Forecast Chart */}
          <Card className="stats-card mb-8" data-testid="forecast-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="w-5 h-5 text-yellow-500" />
                Prévisions de production (7 jours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorProd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis unit=" kWh" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px'
                      }}
                      formatter={(value, name) => [
                        `${value.toFixed(1)} kWh`, 
                        name === 'production' ? 'Production' : 'Confiance'
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="production"
                      stroke="#f59e0b"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorProd)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Weather Cards */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Calendrier des créneaux optimaux
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {schedule.slice(0, 4).map((day, index) => (
                <Card 
                  key={index} 
                  className={`border-2 transition-all hover:shadow-lg ${getWeatherBg(solarForecast[index]?.weather?.weather_code)}`}
                  data-testid={`schedule-day-${index}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-foreground">
                        {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric' })}
                      </span>
                      {getWeatherIcon(solarForecast[index]?.weather?.weather_code)}
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-2xl font-bold text-primary">{day.expected_production_kwh.toFixed(1)} kWh</p>
                      <p className="text-xs text-muted-foreground">Production estimée</p>
                    </div>
                    
                    <div className="p-3 bg-white/80 rounded-lg mb-3">
                      <p className="text-sm text-foreground">{day.recommendation}</p>
                    </div>
                    
                    {day.optimal_consumption_hours.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>Optimaux: {day.optimal_consumption_hours.slice(0, 3).join(', ')}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Carbon Footprint & Comparison */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Carbon Footprint */}
            {carbonFootprint && (
              <Card className="stats-card" data-testid="carbon-footprint">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-500" />
                    Bilan Carbone
                  </CardTitle>
                  <CardDescription>Votre impact environnemental positif</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-6">
                    <p className="text-5xl font-bold text-primary">{carbonFootprint.total_co2_saved_kg}</p>
                    <p className="text-lg text-muted-foreground">kg CO₂ évités</p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                      <TreeDeciduous className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-xl font-bold text-foreground">{carbonFootprint.equivalent_trees_planted}</p>
                      <p className="text-xs text-muted-foreground">arbres plantés</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <Car className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-xl font-bold text-foreground">{carbonFootprint.equivalent_car_km_avoided}</p>
                      <p className="text-xs text-muted-foreground">km évités</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-xl">
                      <Plane className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-xl font-bold text-foreground">{carbonFootprint.equivalent_flights_avoided}</p>
                      <p className="text-xs text-muted-foreground">vols évités</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-primary/5 rounded-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground">Énergie renouvelable</span>
                      <span className="font-bold text-primary">{carbonFootprint.renewable_energy_percent}%</span>
                    </div>
                    <div className="w-full h-3 bg-muted rounded-full mt-2 overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${carbonFootprint.renewable_energy_percent}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grid Comparison */}
            {comparison && comparison.local_consumption_kwh > 0 && (
              <Card className="stats-card" data-testid="grid-comparison">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Comparaison Réseau
                  </CardTitle>
                  <CardDescription>Vos économies vs le réseau national (30 jours)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
                    <p className="text-sm text-muted-foreground mb-1">Économies réalisées</p>
                    <p className="text-5xl font-bold text-green-600">+{comparison.savings_euros}€</p>
                    <p className="text-lg text-green-600 font-medium">-{comparison.savings_percent}% vs réseau</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                      <span className="text-sm text-muted-foreground">Consommation locale</span>
                      <span className="font-semibold text-foreground">{comparison.local_consumption_kwh} kWh</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                      <span className="text-sm text-muted-foreground">Coût local</span>
                      <span className="font-semibold text-primary">{comparison.local_cost_euros}€</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                      <span className="text-sm text-muted-foreground">Équivalent réseau</span>
                      <span className="font-semibold text-red-500 line-through">{comparison.grid_equivalent_cost_euros}€</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                      <span className="text-sm text-muted-foreground">Prix moyen local</span>
                      <span className="font-semibold text-foreground">{comparison.average_local_price}€/kWh</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                      <span className="text-sm text-muted-foreground">Prix réseau EDF</span>
                      <span className="font-semibold text-foreground">{comparison.average_grid_price}€/kWh</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {(!comparison || comparison.local_consumption_kwh === 0) && (
              <Card className="stats-card" data-testid="no-comparison">
                <CardContent className="p-8 text-center">
                  <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Pas encore de données</h3>
                  <p className="text-muted-foreground mb-4">
                    Commencez à échanger de l'énergie pour voir vos économies
                  </p>
                  <Button onClick={() => navigate('/communities')} className="btn-primary">
                    Rejoindre une communauté
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Forecasts;
